import dayjs from 'dayjs';

import { MapUtils } from '../../../utility/map-utils.js';

import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';

import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneData, isSameData } from '../../dto/data-model-utils.js';

import { BusinessModuleRef } from '../business-module-ref.js';
import { QrObjectReference, QrObjectType } from './qr-object-reference.js';

export class GenericProductQrObjectReference extends QrObjectReference {
  static readonly TYPE_ID: string = 'd1e595ea0bf7eb56b12709f105819ffa';

  public readonly type: QrObjectType = QrObjectType.GenericProduct;

  private constructor(
    public readonly uniqueId: string,
    public readonly scanCode: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly orderId: string,
    public readonly productItemIndex: number,
    public readonly productInstance: number,
  ) {
    super();
  }

  get $typeId(): string {
    return GenericProductQrObjectReference.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    scanCode: string,
    creationDate: dayjs.Dayjs,
    businessRef: BusinessModuleRef,
    eventId: string,
    orderId: string,
    productItemIndex: number,
    productInstance: number,
  }): GenericProductQrObjectReference {
    return new GenericProductQrObjectReference(
        args.uniqueId,
        args.scanCode,
        args.creationDate,
        args.businessRef,
        args.eventId,
        args.orderId,
        args.productItemIndex,
        args.productInstance,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericProductQrObjectReference {
    return GenericProductQrObjectReference.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      scanCode: MapUtils.getString(map, 'scanCode'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      eventId: MapUtils.getString(map, 'eventId'),
      orderId: MapUtils.getString(map, 'orderId'),
      productItemIndex: MapUtils.getInteger(map, 'productItemIndex'),
      productInstance: MapUtils.getInteger(map, 'productInstance'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'scanCode': this.scanCode,
      'creationDate': context.encodeDate(this.creationDate),
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'orderId': this.orderId,
      'type': fromEnumAsString(this.type),
      'productItemIndex': this.productItemIndex,
      'productInstance': this.productInstance,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    scanCode = this.scanCode,
    creationDate = TimeZone.cloneDate(this.creationDate),
    businessRef = cloneData(this.businessRef),
    eventId = this.eventId,
    orderId = this.orderId,
    productItemIndex = this.productItemIndex,
    productInstance = this.productInstance,
  }: {
    uniqueId?: string,
    scanCode?: string,
    creationDate?: dayjs.Dayjs,
    businessRef?: BusinessModuleRef,
    eventId?: string,
    orderId?: string,
    productItemIndex?: number,
    productInstance?: number,
  } = {}): this {
    return GenericProductQrObjectReference.create({
      uniqueId,
      scanCode,
      creationDate,
      businessRef,
      eventId,
      orderId,
      productItemIndex,
      productInstance,
    }) as this;
  }

  isSame(other: GenericProductQrObjectReference): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.scanCode === this.scanCode &&
      other.creationDate === this.creationDate &&
      isSameData(other.businessRef, this.businessRef) &&
      other.eventId === this.eventId &&
      other.orderId === this.orderId &&
      other.type === this.type &&
      other.productItemIndex === this.productItemIndex &&
      other.productInstance === this.productInstance;
  }

  static verifyType(model: DataModel): boolean {
    return model instanceof GenericProductQrObjectReference;
  }

  static assertType(model: DataModel): model is GenericProductQrObjectReference {
    return model instanceof GenericProductQrObjectReference;
  }
}

export class GenericProductQrObjectReferenceBuilder extends DataModelBuilder<GenericProductQrObjectReference> {
  build(context: SerializationContext, map: SerializedData): GenericProductQrObjectReference {
    return GenericProductQrObjectReference.fromMap(context, map);
  }
}
